import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Text,
  Flex,
  useColorMode,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Image,
  SimpleGrid,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { DownloadIcon, ViewIcon } from "@chakra-ui/icons";
import JSZip from "jszip";
import api from "../config/axios";
import { normalizeQuestionType } from "../lib/normalizeQuestionType";

export default function CsvUploader() {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [csvFileName, setCsvFileName] = useState(null);
  const [showUploader, setShowUploader] = useState(false); // State to toggle visibility
  const [imageZipFileName, setImageZipFileName] = useState(null); // Store extracted image zip filename
  const [images, setImages] = useState([]); // Store images from API
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();
  const { colorMode } = useColorMode();

  // Dynamic styles based on color mode
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBgColor = useColorModeValue("gray.50", "gray.700");

  // Handle file input selection
  const handleFileUpload = (event) => {
    setFile(event.target.files[0]);
  };

  // Handle cell data change
  const handleCellChange = (rowIndex, colKey, value) => {
    const updatedData = [...editedData];
    updatedData[rowIndex][colKey] = value;
    setEditedData(updatedData);
  };

  // Check if a column should be displayed based on the row data
  const shouldDisplayColumn = (colKey, row) => {
    // Hide answer column completely for MRI questions, even if it has an empty string
    if (colKey === "answer" && row.question_type === "mri") {
      return false;
    }

    // For columns that should be conditionally hidden based on content
    if (
      (row.question_type === "fill_blanks" ||
        row.question_type === "descriptive") &&
      (colKey.startsWith("option_") || colKey.startsWith("mri_answer_"))
    ) {
      // Only show if the value is not empty
      return row[colKey] && row[colKey].trim() !== "";
    }

    return true;
  };

  // Upload file and fetch CSV data from the server
  const uploadFile = async () => {
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Using the new API endpoint
      const response = await api.post(
        "/convert_to_structured_format",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Extract file name from the path
      const fullPath = response.data.csv_file_path;
      const fileName = fullPath.split("/").pop();
      setCsvFileName(fileName);

      // Extract image zip filename from image_zip_path
      if (response.data.image_zip_path) {
        const imageZipPath = response.data.image_zip_path;
        // Extract filename after image_file_storage/
        const imageFileStorageIndex = imageZipPath.indexOf("image_file_storage");
        if (imageFileStorageIndex !== -1) {
          // Get the part after "image_file_storage/"
          const pathAfterStorage = imageZipPath.substring(imageFileStorageIndex + "image_file_storage".length);
          // Remove leading slashes or backslashes
          const extractedFileName = pathAfterStorage.replace(/^[\/\\]+/, "");
          setImageZipFileName(extractedFileName);
        } else {
          // Fallback: try to extract just the filename
          const fileNameFromPath = imageZipPath.split(/[\/\\]/).pop();
          setImageZipFileName(fileNameFromPath);
        }
      }

      // If we need to show a preview immediately, we can use the sample_data
      if (response.data.sample_data) {
        // Show loading text
        setCsvData({ csv_data: response.data.sample_data });
        setEditedData(response.data.sample_data);
      }

      try {
        // Fetch the full CSV data
        const csvResponse = await api.get(
          `/display_csv?csv_file_name=${fileName}`
        );

        // Process the response
        if (csvResponse.data && csvResponse.data.csv_data) {
          // Standard response format
          setCsvData(csvResponse.data);
          setEditedData(csvResponse.data.csv_data);
        } else {
          // Direct data format (if csv_data is not wrapped)
          setCsvData({ csv_data: csvResponse.data });
          setEditedData(csvResponse.data);
        }
      } catch (csvError) {
        console.error("Error fetching complete CSV data:", csvError);
        // If we can't get the full data, at least we have the sample data
        if (response.data.sample_data) {
          alert("Could not fetch complete data. Using sample data instead.");
        } else {
          alert("Error fetching data from the server.");
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error uploading file or fetching CSV data:", error);
      alert("Error processing the file. Please try again.");
      setIsLoading(false);
    }
  };

const generateCsv = () => {
  if (!editedData || editedData.length === 0) {
    alert("No data to download");
    return;
  }

  // Get all headers
  const headers = getUniqueColumns();

  // Create CSV content with all headers, fix "question" to "questions" in the header
  const csvHeaders = headers.map(header => header === "question" ? "questions" : header);
  
  const csvContent = [
    csvHeaders.join(","),
    ...editedData.map((row) =>
      headers
        .map((header) => {
          // For MRI questions, ensure answer field is empty
          if (row.question_type === "mri" && header === "answer") {
            return '""';
          }

          // Handle null, undefined, or empty values
          let value =
            row[header] !== null && row[header] !== undefined
              ? row[header]
              : "";

          if (header === "question_type") {
            value = normalizeQuestionType(value);
          }

          // Wrap ALL text fields in quotes to handle commas, line breaks, and special characters
          return typeof value === "string"
            ? `"${value.replace(/"/g, '""')}"` // Escape quotes by doubling them
            : value;
        })
        .join(",")
    ),
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "questions.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  // Get unique columns from all rows to account for varying structures in MRI vs MCQ questions
  const getUniqueColumns = () => {
    if (!editedData || editedData.length === 0) return [];

    const allColumns = new Set();
    editedData.forEach((row) => {
      Object.keys(row).forEach((key) => allColumns.add(key));
    });

    return Array.from(allColumns);
  };

  // Fetch images from the download_image_zip API
  const fetchImages = async () => {
    if (!imageZipFileName) {
      alert("No image zip file available.");
      return;
    }

    setIsLoadingImages(true);
    setImageError(null);
    setImages([]);

    try {
      const response = await api.post("/download_image_zip", {
        file_name: imageZipFileName,
      });

      if (response.data && response.data.images) {
        setImages(response.data.images);
        onImageModalOpen();
      } else {
        setImageError("No images found in the response.");
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setImageError(error.response?.data?.message || "Failed to fetch images. Please try again.");
    } finally {
      setIsLoadingImages(false);
    }
  };

  const base64ToUint8Array = (base64String) => {
    const cleaned = base64String.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
    const byteCharacters = atob(cleaned);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Uint8Array(byteNumbers);
  };

  // Download a single image
  const downloadImage = (imageData, questionNo) => {
    try {
      const byteArray = base64ToUint8Array(imageData);
      const blob = new Blob([byteArray], { type: "image/png" });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${questionNo}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download image.");
    }
  };

  // Helper function to format question number with suffix for duplicates
  const formatQuestionNumber = (questionNo, occurrenceIndex) => {
    if (occurrenceIndex === 0) {
      return questionNo;
    }
    return `${questionNo}(${occurrenceIndex})`;
  };

  const downloadAllImagesAsZip = async () => {
    if (!images.length) {
      alert("No images available to download.");
      return;
    }
    setIsDownloadingZip(true);
    try {
      const zip = new JSZip();
      // Track occurrences of each question number to handle duplicates
      const questionCountMap = new Map();
      
      images.forEach((imageItem, index) => {
        const questionNo = imageItem.question_no || (index + 1).toString();
        const count = questionCountMap.get(questionNo) || 0;
        questionCountMap.set(questionNo, count + 1);
        
        // Format question number with suffix: 16, 16(1), 16(2), etc.
        const formattedQuestionNo = formatQuestionNumber(questionNo, count);
        const fileName = `${formattedQuestionNo}.png`;
        
        zip.file(fileName, base64ToUint8Array(imageItem.image_data));
      });

      const blob = zip.generateAsync
        ? await zip.generateAsync({ type: "blob" })
        : await new Promise((resolve, reject) => {
            try {
              const legacyBlob = zip.generate({ type: "blob" });
              resolve(legacyBlob);
            } catch (legacyError) {
              reject(legacyError);
            }
          });
      // Use the original uploaded file name (without extension) for the zip
      const baseName =
        (file?.name && file.name.replace(/\.[^/.]+$/, "")) ||
        (imageZipFileName && imageZipFileName.replace(/\.zip$/i, "").replace(/_images$/, "")) ||
        "questions";
      const zipFileName = `${baseName}.zip`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error creating zip:", error);
      alert("Failed to create image zip. Please try again.");
    } finally {
      setIsDownloadingZip(false);
    }
  };

  return (
    <Box>
      {/* Button to toggle CsvUploader visibility */}
      <Flex justifyContent="flex-end" mb={4}>
        <Button
          onClick={() => setShowUploader(!showUploader)}
          colorScheme="blue"
        >
          {showUploader ? "Hide CSV Converter" : "Open CSV Converter"}
        </Button>
      </Flex>

      {/* Use Modal for better UI containment */}
      <Modal
        isOpen={showUploader}
        onClose={() => setShowUploader(false)}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg={bgColor} color={textColor}>
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            CSV Converter
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={4}>
            <VStack spacing={4} align="stretch">
              {/* File input field */}
              <Flex direction={{ base: "column", md: "row" }} gap={4}>
                <Input
                  type="file"
                  onChange={handleFileUpload}
                  bg={useColorModeValue("white", "gray.700")}
                  border="1px"
                  borderColor={borderColor}
                  p={2}
                  flex={1}
                  isDisabled={isLoading}
                  opacity={isLoading ? 0.6 : 1}
                />
                <Button
                  onClick={uploadFile}
                  colorScheme="teal"
                  isLoading={isLoading}
                  isDisabled={!file || isLoading}
                  width={{ base: "full", md: "auto" }}
                  loadingText="Processing..."
                >
                  Upload and Convert
                </Button>
              </Flex>

              {/* Loading indicator */}
              {isLoading && (
                <Box
                  mt={4}
                  p={8}
                  borderWidth="2px"
                  borderColor="teal.300"
                  borderRadius="lg"
                  bg={useColorModeValue("teal.50", "teal.900")}
                  textAlign="center"
                >
                  <VStack spacing={4}>
                    <Spinner
                      thickness="4px"
                      speed="0.65s"
                      emptyColor="gray.200"
                      color="teal.500"
                      size="xl"
                    />
                    <VStack spacing={2}>
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        color={useColorModeValue("teal.700", "teal.200")}
                      >
                        Processing your file...
                      </Text>
                      <Text
                        fontSize="sm"
                        color={useColorModeValue("gray.600", "gray.300")}
                      >
                        This may take a few moments. Please wait while we convert your file to structured format.
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              )}

              {/* Display editable CSV data in table */}
              {!isLoading && csvData && editedData && editedData.length > 0 ? (
                <Box
                  mt={4}
                  overflowX="auto"
                  maxH="70vh"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <Table
                    variant="simple"
                    size="sm"
                    style={{ tableLayout: "fixed" }}
                  >
                    <Thead
                      position="sticky"
                      top={0}
                      zIndex={1}
                      bg={headerBgColor}
                    >
                      <Tr>
                        {getUniqueColumns().map((header, idx) => (
                          <Th
                            key={idx}
                            fontSize="xs"
                            py={3}
                            width={
                              header === "question"
                                ? "250px"
                                : header === "answer"
                                ? "200px"
                                : header === "option_1" ||
                                  header === "option_2" ||
                                  header === "option_3" ||
                                  header === "option_4"
                                ? "150px"
                                : "120px"
                            }
                            sx={{ fontFamily: "'Open Sans', sans-serif" }}
                          >
                            {header === "question" ? "questions" : header}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>

                    <Tbody>
                      {editedData.map((row, rowIndex) => (
                        <Tr key={rowIndex}>
                          {getUniqueColumns().map((colKey, colIndex) => {
                            // For MRI questions, don't show the answer cell at all
                            if (
                              colKey === "answer" &&
                              row.question_type === "mri"
                            ) {
                              return (
                                <Td key={colIndex} px={2} py={1}>
                                  {/* Empty cell for MRI answer */}
                                </Td>
                              );
                            }

                            // Skip rendering cells for columns that should be hidden for other reasons
                            if (
                              !shouldDisplayColumn(colKey, row) &&
                              !(
                                colKey === "answer" &&
                                row.question_type === "mri"
                              )
                            ) {
                              return null;
                            }

                            // Customize input based on column type
                            const isLongText =
                              colKey === "question" ||
                              colKey === "answer" ||
                              colKey.startsWith("option_") ||
                              colKey.startsWith("mri_answer_");

                            return (
                              <Td key={colIndex} px={2} py={1}>
                                {isLongText ? (
                                  <Input
                                    as="textarea"
                                    value={
                                      row[colKey] !== null &&
                                      row[colKey] !== undefined
                                        ? row[colKey]
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleCellChange(
                                        rowIndex,
                                        colKey,
                                        e.target.value
                                      )
                                    }
                                    size="xs"
                                    minH={
                                      colKey === "question" ? "80px" : "40px"
                                    }
                                    minW="100px"
                                    resize="vertical"
                                    bg={useColorModeValue("white", "gray.700")}
                                    _focus={{
                                      borderColor: "blue.500",
                                      boxShadow: "0 0 0 1px blue.500",
                                    }}
                                    isReadOnly={
                                      colKey === "answer" &&
                                      row.question_type === "mri"
                                    }
                                  />
                                ) : (
                                  <Input
                                    value={
                                      row[colKey] !== null &&
                                      row[colKey] !== undefined
                                        ? row[colKey]
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleCellChange(
                                        rowIndex,
                                        colKey,
                                        e.target.value
                                      )
                                    }
                                    size="xs"
                                    minW="100px"
                                    bg={useColorModeValue("white", "gray.700")}
                                    _focus={{
                                      borderColor: "blue.500",
                                      boxShadow: "0 0 0 1px blue.500",
                                    }}
                                    isReadOnly={
                                      colKey === "answer" &&
                                      row.question_type === "mri"
                                    }
                                  />
                                )}
                              </Td>
                            );
                          })}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                !isLoading && (
                  <Box
                    mt={4}
                    p={6}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    textAlign="center"
                  >
                    <Text color={useColorModeValue("gray.600", "gray.400")}>
                      No data available. Please upload a file to get started.
                    </Text>
                  </Box>
                )
              )}

              {/* Action buttons */}
              {!isLoading && csvData && editedData && editedData.length > 0 && (
                <HStack mt={4} spacing={4}>
                  <Button
                    colorScheme="blue"
                    onClick={generateCsv}
                  >
                    Download CSV
                  </Button>
                  {imageZipFileName && (
                    <Button
                      colorScheme="teal"
                      leftIcon={<ViewIcon />}
                      onClick={fetchImages}
                      isLoading={isLoadingImages}
                    >
                      View Images
                    </Button>
                  )}
                </HStack>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        isOpen={isImageModalOpen}
        onClose={onImageModalClose}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg={bgColor} color={textColor}>
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            <Flex align="center" justify="space-between">
              <Text>Question Images Preview</Text>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<DownloadIcon />}
                onClick={downloadAllImagesAsZip}
                isDisabled={isLoadingImages || images.length === 0}
                isLoading={isDownloadingZip}
                loadingText="Preparing..."
              >
                Download All (ZIP)
              </Button>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={4}>
            {isLoadingImages ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" />
              </Flex>
            ) : imageError ? (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {imageError}
              </Alert>
            ) : images.length === 0 ? (
              <Text>No images available.</Text>
            ) : (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.500">
                  Found {images.length} image(s)
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {(() => {
                    // Process images to add display labels with suffixes for duplicates
                    const questionCountMap = new Map();
                    const processedImages = images.map((imageItem, index) => {
                      const questionNo = imageItem.question_no || (index + 1).toString();
                      const count = questionCountMap.get(questionNo) || 0;
                      questionCountMap.set(questionNo, count + 1);
                      const displayQuestionNo = formatQuestionNumber(questionNo, count);
                      return { ...imageItem, displayQuestionNo, occurrenceIndex: count };
                    });
                    
                    return processedImages.map((imageItem, index) => (
                      <Box
                        key={index}
                        borderWidth="1px"
                        borderRadius="md"
                        p={4}
                        bg={useColorModeValue("gray.50", "gray.700")}
                      >
                        <VStack spacing={3} align="stretch">
                          <HStack justify="space-between">
                            <Text fontWeight="bold">
                              {imageItem.displayQuestionNo}
                            </Text>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              leftIcon={<DownloadIcon />}
                              onClick={() =>
                                downloadImage(
                                  imageItem.image_data,
                                  imageItem.displayQuestionNo
                                )
                              }
                            >
                              Download
                            </Button>
                          </HStack>
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          overflow="hidden"
                          bg="white"
                        >
                          <Image
                            src={`data:image/png;base64,${imageItem.image_data}`}
                            alt={`Question ${imageItem.displayQuestionNo} image`}
                            maxH="300px"
                            objectFit="contain"
                            mx="auto"
                          />
                        </Box>
                      </VStack>
                    </Box>
                    ));
                  })()}
                </SimpleGrid>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
